import { AlertCircle, Hash, RotateCcw, Target, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface WorkflowStatsCardsProps {
  totalIssues: number;
  activeIssues: number;
  avgClassificationScore: number | null;
  avgReworkCount: number;
  avgTestPassRate: number | null;
  isLoading?: boolean;
}

export function WorkflowStatsCards({
  totalIssues,
  activeIssues,
  avgClassificationScore,
  avgReworkCount,
  avgTestPassRate,
  isLoading = false,
}: WorkflowStatsCardsProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        {[1, 2, 3, 4, 5].map((i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-4" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-3 w-32 mt-2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
      <Card className="hover-elevate" data-testid="card-total-issues">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Issues</CardTitle>
          <Hash className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalIssues}</div>
          <p className="text-xs text-muted-foreground">
            Issues tracked in database
          </p>
        </CardContent>
      </Card>

      <Card className="hover-elevate" data-testid="card-active-issues">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Active Issues</CardTitle>
          <AlertCircle className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{activeIssues}</div>
          <p className="text-xs text-muted-foreground">
            {activeIssues > 10 ? (
              <span className="text-orange-600">Needs attention</span>
            ) : activeIssues > 0 ? (
              <span className="text-blue-600">Issues in progress</span>
            ) : (
              <span className="text-green-600">All resolved</span>
            )}
          </p>
        </CardContent>
      </Card>

      <Card className="hover-elevate" data-testid="card-avg-score">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Avg Complexity Score</CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {avgClassificationScore !== null ? avgClassificationScore : "N/A"}
          </div>
          <p className="text-xs text-muted-foreground">
            Threshold: 15 (Complex)
          </p>
        </CardContent>
      </Card>

      <Card className="hover-elevate" data-testid="card-avg-rework">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Avg Rework Count</CardTitle>
          <RotateCcw className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{avgReworkCount.toFixed(2)}</div>
          <p className="text-xs text-muted-foreground">
            {avgReworkCount < 1 ? (
              <span className="text-green-600">Good - low rework rate</span>
            ) : avgReworkCount < 2 ? (
              <span className="text-yellow-600">Moderate rework</span>
            ) : (
              <span className="text-red-600">High rework rate</span>
            )}
          </p>
        </CardContent>
      </Card>

      <Card className="hover-elevate" data-testid="card-avg-pass-rate">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Avg Test Pass Rate</CardTitle>
          <Target className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {avgTestPassRate !== null ? `${avgTestPassRate}%` : "N/A"}
          </div>
          <p className="text-xs text-muted-foreground">
            {avgTestPassRate !== null ? (
              avgTestPassRate >= 95 ? (
                <span className="text-green-600">Above target (95%)</span>
              ) : avgTestPassRate >= 85 ? (
                <span className="text-yellow-600">Near target</span>
              ) : (
                <span className="text-red-600">Below target</span>
              )
            ) : (
              "No data yet"
            )}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
